import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SiroccoApiError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { BrandScreen } from '../../components/layout/Screens';
import { useSession } from '../../store/SessionContext';
import { SiroccoLogo } from '../../theme/illustrations';
import { t } from '../../i18n';

/**
 * Ecran de connexion.
 *
 * Il redirige tout seul : un membre qui a deja un spot repart sur son
 * feed, les autres passent par "Choisis ton spot".
 */
export function LoginScreen() {
  const { login } = useSession();
  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [field, setField] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setField(null);
    setPending(true);
    try {
      await login({ identifier, password });
      // La redirection precise est decidee par le garde de route.
      navigate('/app/feed', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.auth.connexion.impossible);
      if (cause instanceof SiroccoApiError && cause.field) setField(cause.field);
    } finally {
      setPending(false);
    }
  }

  return (
    <BrandScreen>
      <div className="auth auth--login">
        <div className="auth__logo">
          <SiroccoLogo />
        </div>

        {/* Pas de titre : deux champs, un bouton « Connexion » et un lien
            « S'inscrire » disent deja ou l'on est. Le mot en capitales ne
            faisait que separer la marque du formulaire. */}
        <form className="auth__form" onSubmit={onSubmit} noValidate>
          <Field
            label={t.auth.champs.email}
            type="text"
            inputMode="email"
            autoComplete="username"
            placeholder={t.auth.champs.exempleEmail}
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            error={field === 'identifier' ? error : null}
          />

          <div className="auth__gap" />

          <Field
            label={t.auth.champs.motDePasse}
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={field === 'password' ? error : null}
          />

          <Link to="/forgot-password" className="auth__forgot">
            {t.auth.connexion.oublie}
          </Link>

          {/* Erreur globale : identifiants refuses, reseau coupe... */}
          {error && !field && <p className="auth__error">{error}</p>}

          <Button type="submit" variant="brand" block loading={pending} className="auth__submit">
            {t.auth.connexion.seConnecter}
          </Button>
        </form>

        <p className="auth__switch">
          {t.auth.connexion.nouveau} <Link to="/register">{t.auth.connexion.inscrire}</Link>
        </p>
      </div>
    </BrandScreen>
  );
}
